<template>
	<!-- LSP-20787 (D18): one outlined pill per action the state offers — the Reschedule recipe in the same card
	     (AppointmentConfirmation.vue:69-78). Update number / Send via email ask the parent to open its inline
	     SmsDeliveryRepairPanel; Resend sends at once (§6 S4). -->
	<div v-if="actions.length > 0" class="sms-delivery-actions d-flex flex-wrap ga-2">
		<v-btn
			v-for="action in actions"
			:key="action"
			:data-test-id="`sms-delivery-action-${action}`"
			rounded="pill"
			color="primary"
			variant="outlined"
			size="small"
			:disabled="resending"
			@click="choose(action)">
			{{ SMS_DELIVERY_ACTION_LABELS[action] }}
		</v-btn>
	</div>
</template>

<script setup lang="ts">
	import { computed, ref } from "vue";
	import { useToast } from "vue-toastification";
	import api from "@/api";
	import type { SmsDeliveryState } from "@/api/index.d";
	import { SMS_DELIVERY_ACTION_LABELS, availableDeliveryActions, type SmsDeliveryAction } from "@/helpers/smsDelivery";
	import { notifyInvitationDelivery, summarizeInvitationDelivery } from "@/helpers/invitationSkipReasons";
	import { getMessageFromError } from "@/helpers/utilities";

	// emailInvitationsAllowed (final review I1): the organization's "Allow Email Invitations" — off hides Send via email.
	const props = withDefaults(defineProps<{ conversationId: string; state: SmsDeliveryState; emailInvitationsAllowed?: boolean }>(), {
		emailInvitationsAllowed: true
	});
	// Bugbot: resent carries the conversation id the send was actually made for, so a caller several hops up
	// (EntityDetailPage) can tell a send that finishes after an in-place session switch apart from a current one.
	const emit = defineEmits<{ (event: "select", mode: "phone" | "email"): void; (event: "resent", conversationId: string): void }>();
	const toast = useToast();

	// B8: a state this table does not know offers nothing (the chip beside it already warned).
	const actions = computed<SmsDeliveryAction[]>(() =>
		availableDeliveryActions(props.state, { emailInvitationsAllowed: props.emailInvitationsAllowed })
	);
	const resending = ref(false);

	function choose(action: SmsDeliveryAction) {
		if (action === "resend-text") {
			void resend();
			return;
		}
		emit("select", action === "update-number-resend" ? "phone" : "email");
	}

	// S4: today's both-channel resend (no channel, D16), no box, no contact write. The conversation id is captured
	// before the await, and `resending` blocks a double click.
	async function resend() {
		if (resending.value) return;
		const conversationId = props.conversationId;
		resending.value = true;
		try {
			const response = await api.sendInvitation(conversationId);
			// LSP-20787 (22017): the no-channel resend sends the email too (D6), so the success line names what was queued.
			const textAndEmailQueued = summarizeInvitationDelivery(response.invitations).delivered.length === 2;
			const message = textAndEmailQueued
				? "The text and the email have been re-sent to the contact."
				: "The text has been re-sent to the contact.";
			notifyInvitationDelivery(toast, response.invitations, message);
			// Also after a partial send: the channel that was not skipped did go out, so the page refetches.
			emit("resent", conversationId);
		} catch (error) {
			// M2: identifiers only — contact-admin forwards console.warn to Datadog, and an API message can echo the contact.
			// errorName: httpStatus/errorCode are both undefined for a non-HTTP failure (network TypeError, code bug); the
			// error's own name still identifies it then.
			const { httpStatus, errorCode } = (error ?? {}) as { httpStatus?: number; errorCode?: string };
			const errorName = (error as { name?: string } | null)?.name;
			console.warn("SmsDeliveryActions: resend failed", { conversationId, httpStatus, errorCode, errorName });
			toast.error(getMessageFromError(error, "The invitation could not be sent. Please try again."));
		} finally {
			resending.value = false;
		}
	}
</script>
